import React, { useEffect, useState } from 'react';
import { arrayOf, bool, func, number, object } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { Icons } from '../../../components';

import FilterComponent from '../FilterComponent';
import FilterPill from './FilterPill';
import PricePill from './PricePill';
import BrowseSortMenu from './BrowseSortMenu';
import { isFilterActive } from './filterBarHelpers';

import css from './FilterBar.module.css';

/**
 * Hybrid filter row (ledger §2, desktop/tablet): [All filters (n)] entry pill,
 * head pills as in-place dropdowns, active overflow fields surfacing as
 * pills, listing total, and Sort right-aligned. Overflow fields otherwise
 * live only in the slide-out FilterPanel.
 */
const FilterBar = props => {
  const {
    className,
    headFilters,
    overflowFilters,
    urlQueryParams,
    facetsParams,
    initialValues,
    getHandleChangedValueFn,
    marketplaceCurrency,
    listingCategories,
    intl,
    totalItems,
    listingsAreLoaded,
    sortByComponent,
    sortConfig,
    onSortBy,
    hasKeywords,
    onOpenPanel,
    onOpenPanelField,
    onClearAll,
  } = props;

  // Tablet (550-1023, prototype): pill taps open the panel pre-scrolled to
  // the field instead of an inline dropdown
  const [isTabletViewport, setIsTabletViewport] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(min-width: 550px) and (max-width: 1023px)');
    const update = () => setIsTabletViewport(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const openOverrideFor = fieldKey =>
    isTabletViewport && onOpenPanelField ? () => onOpenPanelField(fieldKey) : null;

  // n = active fields incl. price incl. the sold toggle (ledger FINAL)
  const soldActive = urlQueryParams.showSoldOut === 'true' || urlQueryParams.showSoldOut === true;
  const activeCount =
    [...headFilters, ...overflowFilters].filter(f => isFilterActive(f, urlQueryParams)).length +
    (soldActive ? 1 : 0);
  const activeOverflow = overflowFilters.filter(f => isFilterActive(f, urlQueryParams));

  return (
    <div className={classNames(css.root, className)}>
      <button type="button" className={css.allFiltersButton} onClick={onOpenPanel}>
        <Icons name="filter" />
        <FormattedMessage id="FilterBar.allFilters" />
        {activeCount > 0 ? <span className={css.allFiltersCount}>({activeCount})</span> : null}
      </button>

      {[...headFilters, ...activeOverflow].map(filterConfig =>
        ['enum', 'multi-enum'].includes(filterConfig.schemaType) ? (
          <FilterPill
            key={`FilterBar.${filterConfig.scope || 'built-in'}.${filterConfig.key}`}
            config={filterConfig}
            urlQueryParams={urlQueryParams}
            facetsParams={facetsParams}
            getHandleChangedValueFn={getHandleChangedValueFn}
            totalItems={totalItems}
            onOpenOverride={openOverrideFor(filterConfig.key)}
          />
        ) : filterConfig.schemaType === 'price' ? (
          <PricePill
            key="FilterBar.price"
            label={filterConfig.label || intl.formatMessage({ id: 'FilterBar.priceLabel' })}
            urlQueryParams={urlQueryParams}
            getHandleChangedValueFn={getHandleChangedValueFn}
            totalItems={totalItems}
            onOpenOverride={openOverrideFor('price')}
          />
        ) : (
          <FilterComponent
            key={`FilterBar.${filterConfig.scope || 'built-in'}.${filterConfig.key}`}
            idPrefix="FilterBar"
            className={css.pill}
            config={filterConfig}
            marketplaceCurrency={marketplaceCurrency}
            urlQueryParams={urlQueryParams}
            initialValues={initialValues}
            getHandleChangedValueFn={getHandleChangedValueFn}
            listingCategories={listingCategories}
            intl={intl}
            showAsPopup
            liveEdit={false}
            isDesktop
            contentPlacementOffset={-14}
          />
        )
      )}

      {activeCount > 0 && onClearAll ? (
        <button type="button" className={css.clearAll} onClick={onClearAll}>
          <FormattedMessage id="FilterBar.clearAll" />
        </button>
      ) : null}

      <div className={css.rightGroup}>
        {listingsAreLoaded && totalItems != null ? (
          <span className={css.resultCount}>
            <FormattedMessage id="FilterBar.listingCount" values={{ count: totalItems }} />
            {activeCount > 0 ? (
              <FormattedMessage id="FilterBar.activeFilterSuffix" values={{ count: activeCount }} />
            ) : null}
          </span>
        ) : null}
        {sortConfig && onSortBy ? (
          <BrowseSortMenu
            sortConfig={sortConfig}
            urlQueryParams={urlQueryParams}
            onSortBy={onSortBy}
            hasKeywords={hasKeywords}
          />
        ) : (
          sortByComponent
        )}
      </div>
    </div>
  );
};

FilterBar.defaultProps = {
  className: null,
  headFilters: [],
  overflowFilters: [],
  totalItems: null,
  listingsAreLoaded: false,
  sortByComponent: null,
};

FilterBar.propTypes = {
  headFilters: arrayOf(object),
  overflowFilters: arrayOf(object),
  urlQueryParams: object.isRequired,
  initialValues: func.isRequired,
  getHandleChangedValueFn: func.isRequired,
  onOpenPanel: func.isRequired,
  totalItems: number,
  listingsAreLoaded: bool,
};

export default FilterBar;
